import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Image, BookOpen } from 'lucide-react';
import { useSimpleMode } from '@/contexts/SimpleModeContext';

interface ModeSelectionProps {
  onComplete: () => void;
}

export const ModeSelection = ({ onComplete }: ModeSelectionProps) => {
  const { t } = useTranslation();
  const { setSimpleMode } = useSimpleMode();
  const [selected, setSelected] = useState<'simple' | 'full' | null>(null);

  const handleSelect = (mode: 'simple' | 'full') => {
    setSelected(mode);
    setSimpleMode(mode === 'simple');
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(20);
    }
    
    // Auto-advance after 300ms
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-primary/20 via-background to-primary-light/20">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-2xl font-bold text-foreground mb-2">
          {t('onboarding.modeSelection.title')}
        </h1>
      </motion.div>

      <div className="grid grid-cols-2 gap-6 w-full max-w-md">
        {/* Simple Mode Card */}
        <motion.button
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleSelect('simple')}
          className={`
            relative flex flex-col items-center justify-center p-6 rounded-2xl
            min-h-[200px] transition-all duration-300
            ${selected === 'simple' 
              ? 'bg-primary text-primary-foreground ring-4 ring-primary/50' 
              : 'bg-card text-card-foreground border-2 border-border hover:border-primary/50'
            }
          `}
        >
          <motion.div
            animate={selected === 'simple' ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <Image className="w-16 h-16 mb-4" strokeWidth={1.5} />
          </motion.div>
          <span className="text-lg font-semibold text-center">
            {t('onboarding.modeSelection.simple')}
          </span>
          
          {selected === 'simple' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-3 right-3 w-6 h-6 bg-primary-foreground rounded-full flex items-center justify-center"
            >
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
          )}
        </motion.button>

        {/* Full Mode Card */}
        <motion.button
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleSelect('full')}
          className={`
            relative flex flex-col items-center justify-center p-6 rounded-2xl
            min-h-[200px] transition-all duration-300
            ${selected === 'full' 
              ? 'bg-primary text-primary-foreground ring-4 ring-primary/50' 
              : 'bg-card text-card-foreground border-2 border-border hover:border-primary/50'
            }
          `}
        >
          <motion.div
            animate={selected === 'full' ? { scale: [1, 1.2, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <BookOpen className="w-16 h-16 mb-4" strokeWidth={1.5} />
          </motion.div>
          <span className="text-lg font-semibold text-center">
            {t('onboarding.modeSelection.full')}
          </span>
          
          {selected === 'full' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-3 right-3 w-6 h-6 bg-primary-foreground rounded-full flex items-center justify-center"
            >
              <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>
          )}
        </motion.button>
      </div>
    </div>
  );
};
